import React, { useState, useMemo } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { today, uuid, mapCrossSellOpportunity } from '../../core/helpers';
import { sb } from '../../core/db';
import { DEFAULT_MARKETPLACE_SERVICES } from '../../core/constants';
import { ORG_JSON_KEYS, persistOrgJsonSetting, normalizeMarketplaceCatalogs, clientPurchasedBundleIds } from '../../core/org-json-settings';
import { Icon, InlineIcon } from '../../components/Icon';

export { DEFAULT_MARKETPLACE_SERVICES };

const CATEGORY_COLORS = {
  GST: "#2563EB",
  Compliance: "#7C3AED",
  Tax: "#059669",
  Trademark: "#DC2626",
  "Startup India": "#EA580C",
  MSME: "#0891B2",
  Payroll: "#B45309",
};

const svcIconName = (svc) => svc.icon || "package";
const svcColor = (svc) => CATEGORY_COLORS[svc.category] || "var(--navy)";

async function persistCatalogs(catalogs, setOrg) {
  await persistOrgJsonSetting(ORG_JSON_KEYS.marketplaceCatalogs, catalogs, setOrg, 'marketplaceCatalogs');
  const def = catalogs.find(c => c.isDefault) || catalogs[0];
  setOrg(prev => ({ ...prev, marketplaceServices: def?.services || DEFAULT_MARKETPLACE_SERVICES }));
}

function MarketplacePage() {
  const { clients, employees, showToast, user, dbCreateTicket, visibleCompanyIds, org, setOrg, setCrossSellOpps, bundles, invoices, runSubmitting, submitBusy } = useApp();
  const catalogs = useMemo(
    () => normalizeMarketplaceCatalogs(org?.marketplaceCatalogs, org?.marketplaceServices),
    [org?.marketplaceCatalogs, org?.marketplaceServices]
  );
  const clientCatalogFromBundle = useMemo(() => {
    if (user?.role !== "client") return null;
    for (const cid of visibleCompanyIds || []) {
      for (const bid of clientPurchasedBundleIds(cid, invoices, bundles)) {
        const b = (bundles || []).find(x => x.id === bid);
        if (b?.marketplaceCatalogId) return b.marketplaceCatalogId;
      }
    }
    return null;
  }, [user?.role, visibleCompanyIds, invoices, bundles]);
  const [catalogId, setCatalogId] = useState(() =>
    clientCatalogFromBundle || catalogs.find(c => c.isDefault)?.id || catalogs[0]?.id || 'cat_default'
  );
  const activeCatalog = catalogs.find(c => c.id === catalogId) || catalogs[0];
  const services = activeCatalog?.services?.length ? activeCatalog.services : DEFAULT_MARKETPLACE_SERVICES;

  const [cart, setCart] = useState([]);
  const [selCat, setSelCat] = useState("All");
  const [selClient, setSelCli] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showNewCatalog, setShowNewCatalog] = useState(false);
  const [catalogName, setCatalogName] = useState("");
  const [draft, setDraft] = useState({ category: "GST", name: "", price: "", desc: "", shortName: "", icon: "package" });

  const categories = ["All", ...new Set(services.map(s => s.category))];
  const visible = selCat === "All" ? services : services.filter(s => s.category === selCat);
  const isAdmin = user?.role === "admin";

  const updateCatalogServices = async (nextServices) => {
    const next = catalogs.map(c => c.id === activeCatalog.id ? { ...c, services: nextServices } : c);
    await persistCatalogs(next, setOrg);
  };

  const addToCart = (svc) => {
    if (cart.find(c => c.id === svc.id)) { setCart(c => c.filter(x => x.id !== svc.id)); return; }
    setCart(c => [...c, svc]);
  };

  const submitCart = async () => {
    const isClient = user?.role === "client";
    const cId = isClient ? (visibleCompanyIds?.[0] || clients[0]?.id) : selClient;
    if (!isClient && !selClient) { showToast("Select a client first", "error"); return; }
    if (cart.length === 0) { showToast("Add at least one service to cart", "error"); return; }
    const clientName = clients.find(c => c.id === cId)?.name || user?.name || "Client";
    const list = cart.map(c => c.name).join(", ");
    const assignee = clients.find(c => c.id === cId)?.assignedTo || employees.find(e => e.role === "sales")?.id || employees[0]?.id || "";

    const doSubmit = async () => {
      await dbCreateTicket({
        id: uuid(),
        ticketNo: "SR-" + Date.now().toString(36).toUpperCase(),
        clientId: cId, clientName, raisedBy: user?.id || "",
        subject: `Service request (${activeCatalog?.name || "Marketplace"}): ${list}`,
        description: `Requested from catalog "${activeCatalog?.name}": ${list} (₹${cart.reduce((s, c) => s + c.price, 0).toLocaleString("en-IN")})`,
        priority: "medium", status: "open",
        assignedTo: assignee,
        createdAt: today(), updatedAt: today(), responses: [], tat: null,
      });
      if (isClient) {
        for (const item of cart) {
          const row = {
            id: uuid(),
            company_id: cId,
            client_name: clientName,
            service_name: item.name,
            notes: `Requested by client via ${activeCatalog?.name || "marketplace"} (₹${item.price.toLocaleString("en-IN")})`,
            created_by_name: user?.name || clientName,
            source: "marketplace",
          };
          const res = await sb.insert("cross_sell_opportunities", row);
          if (res?.length) setCrossSellOpps(opps => [...opps, mapCrossSellOpportunity(res[0] || row)]);
        }
      }
      showToast(`${cart.length} service(s) submitted!`, "success");
      setSubmitted(true);
      setCart([]);
    };

    if (runSubmitting) await runSubmitting('Submitting to sales…', doSubmit, 'marketplace');
    else await doSubmit();
  };

  const saveNewItem = async () => {
    if (!draft.name.trim() || !draft.price) { showToast("Name and price required", "error"); return; }
    const item = {
      id: "ms_" + uuid().slice(0, 8),
      category: draft.category,
      name: draft.name.trim(),
      price: Number(draft.price) || 0,
      desc: draft.desc.trim() || draft.name.trim(),
      shortName: draft.shortName.trim() || draft.name.trim().slice(0, 12),
      icon: draft.icon || "package",
    };
    try {
      await updateCatalogServices([...services, item]);
      showToast("Marketplace item added", "success");
      setShowAdd(false);
      setDraft({ category: "GST", name: "", price: "", desc: "", shortName: "", icon: "package" });
    } catch {
      showToast("Could not save item", "error");
    }
  };

  const createCatalog = async () => {
    if (!catalogName.trim()) { showToast("Catalog name required", "error"); return; }
    const cat = {
      id: "cat_" + uuid().slice(0, 8),
      name: catalogName.trim(),
      desc: "",
      isDefault: catalogs.length === 0,
      services: [],
    };
    try {
      await persistCatalogs([...catalogs, cat], setOrg);
      setCatalogId(cat.id);
      setCatalogName("");
      setShowNewCatalog(false);
      showToast(`Catalog "${cat.name}" created`, "success");
    } catch {
      showToast("Could not create catalog", "error");
    }
  };

  const linkCatalogToBundle = async (bundleId) => {
    if (!bundleId || !activeCatalog) return;
    const b = (bundles || []).find(x => x.id === bundleId);
    if (!b) return;
    const payload = { ...b, marketplaceCatalogId: activeCatalog.id };
    try {
      await sb.updateBundle(b.id, payload);
      showToast(`"${activeCatalog.name}" linked to bundle "${b.name}"`, "success");
    } catch {
      showToast("Could not link catalog to bundle", "error");
    }
  };

  if (submitted) return (
    <div className="card" style={{ maxWidth: 500, margin: "40px auto" }}>
      <div className="card-body" style={{ textAlign: "center", padding: "40px 30px" }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}><Icon name="sparkles" size={40} /></div>
        <div style={{ fontSize: 24, marginBottom: 8 }}>Submitted to Sales Team!</div>
        <button className="btn btn-primary" type="button" onClick={() => setSubmitted(false)}>Browse More Services</button>
      </div>
    </div>
  );

  return (
    <div className="marketplace-page">
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {user?.role !== "client" && (
          <>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>Catalog:</span>
            {catalogs.map(c => (
              <button key={c.id} type="button" className={`chip ${catalogId === c.id ? "on" : ""}`} onClick={() => { setCatalogId(c.id); setCart([]); }}>
                {c.name}{c.isDefault ? " ★" : ""}
              </button>
            ))}
          </>
        )}
        {user?.role === "client" && activeCatalog && (
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>{activeCatalog.name}</span>
        )}
        {isAdmin && (
          <>
            <button className="btn btn-sm" type="button" onClick={() => setShowNewCatalog(true)}>+ New catalog</button>
            <button className="btn btn-primary btn-sm" type="button" onClick={() => setShowAdd(true)}>
              <InlineIcon name="add" size={14} gap={5}>Add item</InlineIcon>
            </button>
            {(bundles || []).length > 0 && (
              <select className="f-select" style={{ width: 200, marginLeft: "auto" }} defaultValue="" onChange={e => { linkCatalogToBundle(e.target.value); e.target.value = ""; }}>
                <option value="">Link catalog to bundle…</option>
                {(bundles || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
          </>
        )}
      </div>

      {cart.length > 0 && (
        <div className="marketplace-cart-shell">
          <div className="marketplace-cart-bar">
            <div className="marketplace-cart-summary">
              <strong>{cart.length} service{cart.length > 1 ? "s" : ""}</strong> in cart · Total: <strong>₹{cart.reduce((s, c) => s + c.price, 0).toLocaleString("en-IN")}</strong>
            </div>
            {user?.role !== "client" && (
              <select className="f-select marketplace-cart-select" value={selClient} onChange={e => setSelCli(e.target.value)}>
                <option value="">Select client for pitch…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <button className="btn btn-gold marketplace-cart-submit" type="button" onClick={submitCart} disabled={submitBusy}>
              {submitBusy ? (
                <span className="btn-spinner-wrap">
                  <span className="btn-spinner" aria-hidden="true" />
                  Submitting…
                </span>
              ) : 'Submit to Sales →'}
            </button>
          </div>
        </div>
      )}

      <div className="chips">
        {categories.map(c => (
          <div key={c} className={`chip ${selCat === c ? "on" : ""}`} onClick={() => setSelCat(c)}>{c}</div>
        ))}
      </div>

      {visible.length === 0 && (
        <div className="empty" style={{ marginTop: 24 }}>
          <div className="empty-icon"><Icon name="package" size={32} /></div>
          <div>No services in this catalog yet — {isAdmin ? "add items above." : "check back soon."}</div>
        </div>
      )}

      <div className="marketplace-grid">
        {visible.map(svc => {
          const inCart = cart.find(c => c.id === svc.id);
          const color = svcColor(svc);
          return (
            <div key={svc.id} className={`card marketplace-card ${inCart ? "in-cart" : ""}`} onClick={() => addToCart(svc)} style={{ cursor: "pointer" }}>
              <div className="marketplace-desktop-content">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name={svcIconName(svc)} size={24} color={color} />
                  </div>
                  <span className="tag tag-gold">{svc.category}</span>
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>{svc.name}</div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14 }}>{svc.desc}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "var(--navy)" }}>₹{svc.price.toLocaleString("en-IN")}</div>
                  <button className={`btn btn-sm ${inCart ? "btn-gold" : "btn-primary"}`} type="button" onClick={(e) => { e.stopPropagation(); addToCart(svc); }}>
                    {inCart ? "In Cart" : "Add"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showAdd && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">New item — {activeCatalog?.name}</div>
              <button type="button" className="modal-close" onClick={() => setShowAdd(false)}><Icon name="close" size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="f-group">
                  <label className="f-label">Category</label>
                  <select className="f-select" value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}>
                    {Object.keys(CATEGORY_COLORS).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="f-group">
                  <label className="f-label">Name <span className="f-req">*</span></label>
                  <input className="f-input" value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} />
                </div>
                <div className="f-group">
                  <label className="f-label">Price (₹) <span className="f-req">*</span></label>
                  <input className="f-input" type="number" min="0" value={draft.price} onChange={e => setDraft(d => ({ ...d, price: e.target.value }))} />
                </div>
                <div className="f-group">
                  <label className="f-label">Description</label>
                  <textarea className="f-textarea" value={draft.desc} onChange={e => setDraft(d => ({ ...d, desc: e.target.value }))} rows={3} />
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" type="button" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" type="button" onClick={saveNewItem}>Save item</button>
            </div>
          </div>
        </div>
      )}

      {showNewCatalog && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowNewCatalog(false)}>
          <div className="modal-box" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">New marketplace catalog</div>
              <button type="button" className="modal-close" onClick={() => setShowNewCatalog(false)}><Icon name="close" size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="f-group">
                <label className="f-label">Catalog name <span className="f-req">*</span></label>
                <input className="f-input" value={catalogName} onChange={e => setCatalogName(e.target.value)} placeholder="e.g. GST Add-ons, Startup Pack" />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" type="button" onClick={() => setShowNewCatalog(false)}>Cancel</button>
              <button className="btn btn-primary" type="button" onClick={createCatalog}>Create catalog</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { MarketplacePage };
